/**
 * User Management Service
 *
 * Handles user self-service operations via TYPO3 backend
 * All operations require CSRF protection and session authentication
 *
 * SECURITY: All requests MUST include:
 * - credentials: 'include' for session cookies
 * - X-CSRF-Token header from auth service
 */

import type Typo3AuthService from './typo3-auth-service';
import type { UserProfile } from '../stores/user-store';
import {
  getRequiredCsrfToken,
  withCsrfRetry,
  assertAuthorized,
  buildProxyHeaders,
} from './auth-utils';
import { pendingIfSessionEnded, suspendIfSessionEnded } from './session-ended';

// API Response types
export interface UserManagementResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface ProfileResponse {
  user: UserProfile;
}

export interface UserManagementServiceOptions {
  typo3BaseUrl?: string;
  authService?: Typo3AuthService | null;
}

class UserManagementService {
  private typo3BaseUrl: string;
  private authService: Typo3AuthService | null;
  private userManagementEndpoint: string;

  constructor(options: UserManagementServiceOptions = {}) {
    this.typo3BaseUrl = options.typo3BaseUrl || 'https://www.gitterdan.ai';
    this.authService = options.authService || null;
    this.userManagementEndpoint = `${this.typo3BaseUrl}/index.php?type=3004`;
  }

  /**
   * Make authenticated request to user management endpoint
   * SECURITY: All requests include CSRF token and credentials
   */
  private async makeRequest<T>(
    command: string,
    data: Record<string, unknown> = {}
  ): Promise<T> {
    const pending = pendingIfSessionEnded<T>();
    if (pending) return pending;

    // A rotated CSRF token is refreshed and the request retried once (#113).
    return withCsrfRetry(this.authService, () =>
      this.sendRequest<T>(command, data)
    );
  }

  /** One request with the current CSRF token. */
  private async sendRequest<T>(
    command: string,
    data: Record<string, unknown>
  ): Promise<T> {
    const csrfToken = getRequiredCsrfToken(this.authService);

    const response = await fetch(this.userManagementEndpoint, {
      method: 'POST',
      credentials: 'include', // MANDATORY: Send session cookies
      headers: buildProxyHeaders(csrfToken), // MANDATORY: CSRF protection
      body: JSON.stringify({
        command,
        ...data,
      }),
    });

    // Handle common auth errors
    try {
      await assertAuthorized(response);
    } catch (error) {
      const suspended = suspendIfSessionEnded<T>(error);
      if (suspended) return suspended;
      throw error;
    }

    // Handle other HTTP errors
    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(
        `User management request failed: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`
      );
    }

    const result = await response.json();

    // Handle API-level errors
    if (result.success === false && result.error) {
      throw new Error(result.error);
    }

    return result.data || result;
  }

  /**
   * Update user profile
   */
  async updateProfile(updates: {
    firstName?: string;
    lastName?: string;
  }): Promise<UserProfile> {
    const response = await this.makeRequest<ProfileResponse>('update_profile', {
      ...updates,
    });
    return response.user;
  }

  // ============================================
  // Password Management
  // ============================================

  /**
   * Get the Ory hosted settings URL where the signed-in user changes their
   * password. Ory asks them to sign in again first; the caller redirects the
   * browser there. (The BFF command keeps its historic name.)
   */
  async getChangePasswordUrl(): Promise<string> {
    const response = await this.makeRequest<{ settingsUrl: string }>(
      'request_password_reset'
    );
    return response.settingsUrl;
  }

  // ============================================
  // Account Settings (Ory hosted)
  // ============================================

  /**
   * Get the Ory hosted account-settings URL.
   * MFA enrollment and management are handled by Ory's Account Experience;
   * the caller redirects the browser to this URL.
   */
  async getAccountSettingsUrl(): Promise<string> {
    const response = await this.makeRequest<{ settingsUrl: string }>(
      'get_account_settings_url'
    );
    return response.settingsUrl;
  }
}

export default UserManagementService;
